export type VentureProductStatus = 'executable-model' | 'gated-pilot' | 'research-mode'

export interface VentureProduct {
  id: string
  name: string
  shortName: string
  workflowCode: string
  status: VentureProductStatus
  audience: string
  promise: string
  enhancement: string
  inputs: string[]
  outputs: string[]
  sharedCapabilities: string[]
  successMetrics: string[]
  defaultObjective: string
  defaultOutcome: string
}

export const ventureRadarWorkflowCode = 'dawn-venture-opportunity-radar'

export const ventureProducts: VentureProduct[] = [
  {
    id: 'intent-radar',
    name: 'DAWN Intent Radar',
    shortName: 'Intent Radar',
    workflowCode: 'dawn-intent-radar-model',
    status: 'executable-model',
    audience: 'SaaS teams, agencies, consultants and high-ticket service firms',
    promise: 'Find public buying signals, explain why they matter and prepare the next best approved action.',
    enhancement: 'Moves beyond keyword alerts by joining intent evidence, account research, opportunity scoring, response preparation and measured follow-up.',
    inputs: ['target market', 'offer', 'approved public sources', 'buyer-language rules', 'suppression policy'],
    outputs: ['ranked intent signals', 'evidence register', 'account brief', 'recommended response', 'CRM-ready opportunity record'],
    sharedCapabilities: ['Firecrawl read-only research', 'SDR evidence scoring', 'Companies House and procurement adapters', 'approval routing'],
    successMetrics: ['qualified signals', 'accepted opportunities', 'time to first review', 'signal-to-meeting conversion'],
    defaultObjective: 'Find and rank commercially credible buying-intent signals for the selected offer using approved public sources only.',
    defaultOutcome: 'A decision-ready list of high-confidence opportunities with evidence, fit score, suggested response and explicit exclusions.',
  },
  {
    id: 'revenue-agent',
    name: 'DAWN Revenue Agent',
    shortName: 'Revenue Agent',
    workflowCode: 'dawn-revenue-agent-model',
    status: 'gated-pilot',
    audience: 'Founder-led B2B companies and commercial teams',
    promise: 'Turn approved prospects and conversations into a governed research-to-meeting and follow-up operating rhythm.',
    enhancement: 'Combines discovery, research, qualification, meeting preparation, follow-up, opportunity risk and evidence rather than acting as another bulk sequencer.',
    inputs: ['approved account list', 'ICP', 'offer', 'mailbox metadata', 'meeting notes', 'suppression ledger'],
    outputs: ['account priorities', 'meeting brief', 'follow-up draft', 'deal-risk flags', 'next-action queue'],
    sharedCapabilities: ['DAWN SDR programme', 'privacy and suppression controls', 'explainable scoring', 'Gmail and Calendar connectors'],
    successMetrics: ['meetings prepared', 'follow-up preparation time', 'accepted drafts', 'pipeline influenced'],
    defaultObjective: 'Review the selected revenue opportunity and prepare the next highest-value action without sending or publishing anything.',
    defaultOutcome: 'A complete account brief, opportunity score, meeting or follow-up package and an approval-ready action plan.',
  },
  {
    id: 'visibility-engine',
    name: 'DAWN Visibility Engine',
    shortName: 'Visibility Engine',
    workflowCode: 'dawn-visibility-engine-model',
    status: 'executable-model',
    audience: 'SaaS, agencies, professional services and specialist ecommerce brands',
    promise: 'Find the questions a market is asking and build an evidence-led plan to earn visibility across search, AI answers and communities.',
    enhancement: 'Connects SEO, AEO, Reddit intelligence, content gaps, internal linking and conversion opportunities into one measurable loop.',
    inputs: ['website', 'approved analytics', 'target market', 'competitor set', 'brand claims', 'content inventory'],
    outputs: ['visibility gap map', 'question clusters', 'content and tool backlog', 'community opportunity list', 'measurement plan'],
    sharedCapabilities: ['market intelligence', 'website audit', 'content engine', 'public-web research', 'case-study evidence'],
    successMetrics: ['priority gaps found', 'briefs approved', 'coverage growth', 'qualified visits and enquiries'],
    defaultObjective: 'Audit the selected market and website for high-value search, AI-answer and community visibility opportunities.',
    defaultOutcome: 'A ranked visibility backlog with evidence, recommended assets, distribution routes and measurable success criteria.',
  },
  {
    id: 'content-multiplier',
    name: 'DAWN Content Multiplier',
    shortName: 'Content Multiplier',
    workflowCode: 'dawn-content-multiplier-model',
    status: 'executable-model',
    audience: 'Brands, creators, agencies, course businesses and ecommerce teams',
    promise: 'Turn one approved source asset into a controlled multi-format campaign package.',
    enhancement: 'Adds brand memory, product truth, rights checks, channel-specific transformation, creative routing and performance feedback to repurposing.',
    inputs: ['source asset', 'brand brain', 'campaign objective', 'approved claims', 'channel list', 'asset rights'],
    outputs: ['repurposing map', 'channel drafts', 'creative briefs', 'asset manifest', 'approval batches'],
    sharedCapabilities: ['Ad Creative Studio', 'creative feedback engine', 'ComfyUI routing', 'Google Flow and Adobe pathways', 'asset provenance QA'],
    successMetrics: ['usable assets produced', 'production time avoided', 'approval rate', 'content reuse ratio'],
    defaultObjective: 'Transform the selected approved source into a complete multi-channel content package while preserving brand and factual accuracy.',
    defaultOutcome: 'A structured campaign package with copy, creative briefs, asset requirements, provenance notes and approval stages.',
  },
  {
    id: 'business-os',
    name: 'DAWN Business OS',
    shortName: 'Business OS',
    workflowCode: 'dawn-business-os-model',
    status: 'gated-pilot',
    audience: 'Founder-led businesses that operate across disconnected inboxes, documents and recurring tasks',
    promise: 'Create one controlled operating view of priorities, repeat work, exceptions, approvals and measurable outcomes.',
    enhancement: 'Does not merely centralise records; it detects exceptions, prepares work and routes decisions through governed workflows.',
    inputs: ['approved business sources', 'workflow state', 'mailbox and calendar metadata', 'budgets', 'service health', 'decision rules'],
    outputs: ['daily command brief', 'exception queue', 'prepared work', 'approval list', 'operating scorecard'],
    sharedCapabilities: ['Founder Command Centre', 'Business Brain', 'operations watchdog', 'usage controls', 'case-study evidence'],
    successMetrics: ['exceptions detected', 'tasks prepared', 'founder hours avoided', 'workflow success rate'],
    defaultObjective: 'Produce an operating brief for the selected business scope, focusing only on priorities, exceptions and work that can be safely prepared.',
    defaultOutcome: 'A single decision-ready operating view with owners, deadlines, blockers, prepared actions and evidence of time avoided.',
  },
  {
    id: 'career-os',
    name: 'DAWN Career OS',
    shortName: 'Career OS',
    workflowCode: 'dawn-career-os-model',
    status: 'gated-pilot',
    audience: 'Experienced commercial professionals seeking a focused and evidence-led job campaign',
    promise: 'Turn a verified career vault into role discovery, fit scoring, tailored applications and interview preparation.',
    enhancement: 'Combines persistent career evidence, role scoring, application adaptation, interview preparation and campaign tracking rather than producing isolated CV drafts.',
    inputs: ['approved career vault', 'target role', 'location and compensation constraints', 'job description', 'application history'],
    outputs: ['role-fit score', 'evidence gaps', 'tailored CV plan', 'application draft', 'interview and negotiation pack'],
    sharedCapabilities: ['Business Brain memory', 'document generation', 'job research', 'evidence scoring', 'approval-first drafts'],
    successMetrics: ['qualified roles found', 'applications approved', 'interviews secured', 'preparation time avoided'],
    defaultObjective: 'Assess the selected role against the approved career vault and prepare the strongest truthful application and interview plan.',
    defaultOutcome: 'A transparent fit score, evidence-backed application package, interview plan and clear reasons to pursue or reject the role.',
  },
]
